import { ECONOMY } from '@/config/economy';
import { FEEDBACK } from '@/config/feedback';
import { LOOT } from '@/config/loot';
import { PLAYER } from '@/config/player';
import { WEAPONS, type WeaponRarity } from '@/config/weapons';
import type { ItemKind } from '@/entities/items';
import { levelForXp, loadTotalXp, saveTotalXp } from '@/core/progress';
import { t } from '@/i18n';
import type { Boss } from '@/bosses/boss-base';
import type { InputManager } from '@/input/input';
import type { World } from '@/scene/world';
import type { Hud } from '@/ui/hud';
import { sfx } from '@/audio/sfx';
import type { RunState } from './run-state';
import { difficultyFor, isSectorVictory } from './sector-hooks';
import { crateDestination } from './ship';

/** Boss-death notifications the run layer reacts to (game.ts wires them). */
export interface RewardEvents {
  /** Any boss fell (achievement checks) */
  bossKilled(): void;
  /** The sector finale fell — the victory flow takes over (no portal) */
  sectorWon(): void;
}

/**
 * What kills and damage pay out (docs/03): the loot meter (damage + minion
 * kills → one 5-value gem at a time), lifesteal, need-based supply drops,
 * shield plates at boss HP thresholds, the boss-death payout/loot roll and
 * account-wide pilot XP. Wave 4: FEWER, bigger pickups, same total value.
 */
export class Rewards {
  /** Account-wide pilot XP (persistent, level cap 999 — docs/01) */
  private totalXp = loadTotalXp();
  pilotLevel = levelForXp(loadTotalXp());

  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly hud: Hud,
    private readonly input: InputManager,
    private readonly events: RewardEvents,
  ) {}

  /**
   * Every point of damage dealt flows through here: hit-drip gems leak from
   * the target, and the Life Drinker card converts a slice into hull HP.
   */
  onDamageDealt(damage: number, x: number, y: number): void {
    const { run, world } = this;
    const player = world.player;
    this.feedMeter(damage, x, y, ECONOMY.dripScatterMul);
    if (player.stats.lifestealPct > 0) {
      const total = damage * player.stats.lifestealPct + run.healFrac;
      const gain = Math.floor(total);
      run.healFrac = total - gain;
      if (gain > 0) player.hp = Math.min(player.maxHp, player.hp + gain);
    }
  }

  /**
   * Loot meter (damage-units): every `dripPerDamage` is 1 yellow of value,
   * paid out as ONE piece of `dripPieceValue` — not a confetti of 1s.
   */
  private feedMeter(units: number, x: number, y: number, scatterMul: number): void {
    const run = this.run;
    const step = ECONOMY.dripPerDamage * ECONOMY.dripPieceValue;
    run.dripAccum += units;
    while (run.dripAccum >= step) {
      run.dripAccum -= step;
      this.world.gems.spawnValue(x, y, ECONOMY.dripPieceValue, run.rng, 'yellow', {
        small: true, scatterMul,
      });
    }
  }

  /** Gems swept up this frame (Greed card: fractional yellow accumulates). */
  collectGems(yellow: number, red: number): void {
    if (yellow <= 0 && red <= 0) return;
    const run = this.run;
    const total = yellow * this.world.player.stats.greedMul + run.gemFrac;
    const gain = Math.floor(total);
    run.gemFrac = total - gain;
    run.gems += gain;
    run.red += red;
    run.stats.gemsCollected += yellow + red;
    if (red > 0) sfx.redGem();
    else sfx.gem();
  }

  rewardMinionKills(killed: ReadonlyArray<{ x: number; y: number }>): void {
    const { world, run } = this;
    const M = LOOT.minion;
    for (const k of killed) {
      world.fx.explosion(k.x, k.y, FEEDBACK.minionBlast);
      // The kill's yellow feeds the loot meter (pays out at the kill spot)
      this.feedMeter(ECONOMY.droneGems * ECONOMY.dripPerDamage, k.x, k.y, 1);
      run.stats.droneKills++;
      this.addXp(M.xp);
      // Rare red core from minions keeps the CORE economy flowing (rebalance)
      if (run.rng.chance(M.redChance)) world.gems.spawnValue(k.x, k.y, M.redValue, run.rng, 'red');
      if (run.rng.chance(M.repairChance)) this.dropItem('repair', k.x, k.y, M.supplyAmount);
      else if (run.rng.chance(M.armorChance)) this.dropItem('armor', k.x, k.y, M.supplyAmount);
    }
  }

  /**
   * Supply drop that means something: a repair on a full hull, a plate for
   * a full store, or one past the on-screen cap pays its yellow value.
   */
  private dropItem(kind: ItemKind, x: number, y: number, amount: number): void {
    const { world, run } = this;
    if (this.useless(kind) || world.items.count >= LOOT.maxLiveItems) {
      world.gems.spawnValue(x, y, LOOT.itemValue[kind] * amount, run.rng, 'yellow');
      return;
    }
    world.items.spawn(kind, x, y, amount);
  }

  private useless(kind: ItemKind): boolean {
    const p = this.world.player;
    return kind === 'repair' ? p.hp >= p.maxHp : this.run.storedArmor >= PLAYER.maxArmor;
  }

  /** Supply pickup: heal / store plates; what no longer fits pays yellow. */
  collectItem(item: { kind: ItemKind; amount: number }): void {
    const { run, world } = this;
    const player = world.player;
    let wasted = 0;
    if (item.kind === 'repair') {
      if (player.hp >= player.maxHp) wasted = item.amount;
      player.hp = Math.min(player.maxHp, player.hp + PLAYER.cellHp * item.amount);
    } else {
      // Not instant (docs/12 H3): plates wait in the hangar until USED
      const room = PLAYER.maxArmor - run.storedArmor;
      wasted = Math.max(0, item.amount - room);
      run.storedArmor = Math.min(PLAYER.maxArmor, run.storedArmor + item.amount);
    }
    if (wasted > 0) run.gems += LOOT.itemValue[item.kind] * wasted;
    sfx.pickup();
  }

  /** Shield drop chance whenever the boss crosses a damage threshold. */
  rollShieldDrops(boss: Boss): void {
    const run = this.run;
    while (run.lootThresholds.length > 0 && boss.hpPct <= run.lootThresholds[0]!) {
      run.lootThresholds.shift();
      if (run.rng.chance(LOOT.shieldChance)) {
        const a = run.rng.range(0, Math.PI * 2);
        const r = LOOT.shieldDropRadius;
        this.dropItem('armor', boss.x + Math.cos(a) * r, boss.y + Math.sin(a) * r, 1);
      }
    }
  }

  onBossDeath(boss: Boss): void {
    const { world, run } = this;
    const player = world.player;
    sfx.bossDeath();
    this.input.rumble(...FEEDBACK.bossDeath.rumble);
    world.fx.bigExplosion(boss.x, boss.y);
    world.camera.shake(FEEDBACK.bossDeath.shake);
    world.minions.clear();
    world.bullets.clearAll(false);
    world.setBossVisible(false);
    run.stats.bossKills++;
    run.stats.arenasCleared++;
    this.addXp(LOOT.bossXp.base + boss.level * LOOT.bossXp.perLevel);
    // Salvage Rig card: scrap the carcass into armor plates (docs/12 C5)
    if (player.stats.armorOnKill > 0) {
      player.armor = Math.min(PLAYER.maxArmor, player.armor + player.stats.armorOnKill);
      this.hud.flashArmor();
    }
    // The played level is recorded in bossNextLevel — it never repeats

    // Reward: jittered gem burst — payouts vary run to run (docs/12 D4)
    const economy = difficultyFor(run).economy;
    const gemTotal = (ECONOMY.bossGemBase + run.roomInSector * ECONOMY.bossGemPerArena) *
      run.rng.range(1 - ECONOMY.bossGemJitter, 1 + ECONOMY.bossGemJitter) * economy;
    world.gems.spawnValue(boss.x, boss.y, gemTotal, run.rng, 'yellow', {
      minPieces: LOOT.bossGemMinBurst,
    });
    // Red gems: the rare currency for tier/epic recipes — scales with boss level
    const redDrop = Math.round(
      (ECONOMY.redBase + Math.floor(boss.level / ECONOMY.redPerLevels)) * economy,
    );
    world.gems.spawnValue(boss.x, boss.y, redDrop, run.rng, 'red', {
      minPieces: LOOT.bossRedMinBurst,
    });
    // Bonus loot is a roll, not a guarantee: crate / shield / repair / nothing
    const lootRoll = run.rng.next();
    const loot = ECONOMY.bossLoot;
    const dropY = boss.y + LOOT.bossDropOffset;
    if (lootRoll < loot.weapon) {
      this.dropCrate(boss.x, dropY);
    } else if (lootRoll < loot.weapon + loot.shield) {
      this.dropItem('armor', boss.x, dropY, 1);
    } else if (lootRoll < loot.weapon + loot.shield + loot.repair) {
      this.dropItem('repair', boss.x, dropY, 1);
    }

    this.events.bossKilled();
    // A won sector finale hands over to the victory flow (no portal)
    if (isSectorVictory(run, boss.def.id)) {
      this.events.sectorWon();
      return;
    }
    world.portal.setActive(true);
    this.hud.notify(t('hud.portalOpen'), 'portal');
  }

  /**
   * Weapon crate — only a weapon the ship does not own, and only if it has
   * somewhere to go (socket, bag or the swap-or-scrap slot). Otherwise the
   * crate's scrap value drops as yellow instead (docs/03 wave 4).
   */
  private dropCrate(x: number, y: number): void {
    const { run, world } = this;
    const r = run.rng.next();
    const odds = LOOT.weaponRarity;
    const rarity: WeaponRarity = r < odds.epic ? 'epic' : r < odds.rare ? 'rare' : 'common';
    const fresh = Object.values(WEAPONS).filter((w) => crateDestination(world.player, run, w.id) !== 'scrap');
    const pool = fresh.filter((w) => w.rarity === rarity);
    if (fresh.length === 0) {
      world.gems.spawnValue(x, y, LOOT.scrapValue[rarity], run.rng, 'yellow');
      return;
    }
    world.drops.spawn(run.rng.pick(pool.length > 0 ? pool : fresh).id, x, y);
  }

  addXp(amount: number): void {
    this.totalXp += amount;
    saveTotalXp(this.totalXp);
    const lvl = levelForXp(this.totalXp);
    if (lvl > this.pilotLevel) {
      this.pilotLevel = lvl;
      sfx.recordToast();
      this.hud.notify(t('hud.levelUp', { n: lvl }), 'level');
    }
  }
}
